import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth service
export const authService = {
  login: async (password) => {
    const response = await api.post('/auth/login', { password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return { token, user };
  },

  logout: () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  },

  verifyToken: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const response = await api.post('/auth/verify');
    return response.data.user;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  initializeAuth: () => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
};

// Apps service
export const appsService = {
  getAll: async () => {
    const response = await api.get('/apps');
    return response.data;
  },

  getOne: async (name) => {
    const response = await api.get(`/apps/${name}`);
    return response.data;
  },

  create: async (appData) => {
    const response = await api.post('/apps', appData);
    return response.data;
  },

  update: async (name, appData) => {
    const response = await api.put(`/apps/${name}`, appData);
    return response.data;
  },

  delete: async (name, options = {}) => {
    const params = new URLSearchParams(options).toString();
    const response = await api.delete(`/apps/${name}?${params}`);
    return response.data;
  },

  getStatus: async (name) => {
    const response = await api.get(`/apps/${name}/status`);
    return response.data;
  }
};

// Git service
export const gitService = {
  getStatus: async () => {
    const response = await api.get('/git/status');
    return response.data;
  },

  getLog: async (count = 10) => {
    const response = await api.get(`/git/log?count=${count}`);
    return response.data;
  },

  getDiff: async () => {
    const response = await api.get('/git/diff');
    return response.data;
  },

  commit: async (message) => {
    const response = await api.post('/git/commit', { message });
    return response.data;
  },

  pull: async () => {
    const response = await api.post('/git/pull');
    return response.data;
  },

  push: async () => {
    const response = await api.post('/git/push');
    return response.data;
  },

  clone: async (force = false) => {
    try {
      const response = await api.post('/git/clone', { force });
      return response.data;
    } catch (error) {
      // 409 means already cloned — return the data so the UI can handle it
      if (error.response?.status === 409) {
        return error.response.data;
      }
      throw error;
    }
  }
};

// MinIO service
export const minioService = {
  listBuckets: async () => {
    const response = await api.get('/minio/buckets');
    return response.data;
  },

  createBucket: async (name, isPublic = false) => {
    const response = await api.post('/minio/buckets', { name, public: isPublic });
    return response.data;
  },

  deleteBucket: async (name, force = false) => {
    const response = await api.delete(`/minio/buckets/${name}?force=${force}`);
    return response.data;
  },

  checkBucketExists: async (name) => {
    const response = await api.get(`/minio/buckets/${name}/exists`);
    return response.data;
  },

  setPublicPolicy: async (name) => {
    const response = await api.post(`/minio/buckets/${name}/policy/public`);
    return response.data;
  },

  createAppBuckets: async (appName) => {
    const response = await api.post(`/minio/apps/${appName}/buckets`);
    return response.data;
  },

  deleteAppBuckets: async (appName, force = false) => {
    const response = await api.delete(`/minio/apps/${appName}/buckets?force=${force}`);
    return response.data;
  }
};

// Database service
export const databaseService = {
  testConnection: async () => {
    const response = await api.get('/database/test');
    return response.data;
  },

  listDatabases: async () => {
    const response = await api.get('/database/databases');
    return response.data;
  },

  createDatabase: async (name) => {
    const response = await api.post('/database/databases', { name });
    return response.data;
  },

  deleteDatabase: async (name) => {
    const response = await api.delete(`/database/databases/${name}`);
    return response.data;
  },

  renameDatabase: async (oldName, newName) => {
    const response = await api.post(`/database/databases/${oldName}/rename`, { newName });
    return response.data;
  },

  getDatabaseInfo: async (name) => {
    const response = await api.get(`/database/databases/${name}`);
    return response.data;
  },

  checkDatabaseExists: async (name) => {
    const response = await api.get(`/database/databases/${name}/exists`);
    return response.data;
  }
};

// ArgoCD service
export const argoService = {
  getApplication: async (name) => {
    const response = await api.get(`/argo/applications/${name}`);
    return response.data;
  },

  restartResource: async (appName, resourceName, params) => {
    const response = await api.post(`/argo/applications/${appName}/restart/${resourceName}`, params);
    return response.data;
  },

  syncApplication: async (name) => {
    const response = await api.post(`/argo/applications/${name}/sync`);
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/argo/settings');
    return response.data;
  }
};

// Axios interceptors for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;